import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, ArrowUpRight, Send, TrendingUp, ArrowDownLeft,
  Eye, EyeOff, RefreshCw, Wallet as WalletIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CURRENCY_BY_CODE } from "@/lib/constants";
import { formatAmount } from "@/lib/format";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import FundWalletModal from "@/components/wallet/FundWalletModal";

const ez = mmEase.expo;

// WES-CUR: Canonical accent colors — DS 2.0 compliant (EUR = #7C3AED, GHS = #EF4444)
const WALLET_ACCENT = {
  GBP: "#10B981",
  USD: "#06B6D4",
  EUR: "#7C3AED",
  CAD: "#F59E0B",
  NGN: "#3B82F6",
  GHS: "#EF4444",
  KES: "#8B5CF6",
  RON: "#0EA5E9",
};

// WES-CUR-C1: Canonical priority order
const CURRENCY_ORDER = ["GBP", "USD", "EUR", "CAD", "NGN", "GHS", "KES", "RON"];

// WES-PRI-02: localStorage-backed privacy mode
function usePrivacyMode() {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("mm-privacy-mode") === "true"; }
    catch { return false; }
  });
  const toggle = () =>
    setHidden(h => {
      const next = !h;
      try { localStorage.setItem("mm-privacy-mode", String(next)); } catch {}
      return next;
    });
  return [hidden, toggle];
}

export default function WalletPage() {
  const [wallets,     setWallets]     = useState([]);
  const [rates,       setRates]       = useState([]);
  const [showTopup,   setShowTopup]   = useState(false);
  const [topupCurrency, setTopupCurrency] = useState("GBP");
  const [pageLoading, setPageLoading] = useState(true);
  const [privacyMode, togglePrivacy]  = usePrivacyMode();
  const [privacyMsg,  setPrivacyMsg]  = useState("");
  const topupTriggerRef = useRef(null);

  // WES-PRI-06: reduced motion check for AnimatePresence transitions
  const reducedMotion =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  const priDuration = reducedMotion ? 0 : 0.15;

  const load = () =>
    Promise.all([api.get("/wallets"), api.get("/fx/rates")])
      .then(([w, r]) => { setWallets(w.data); setRates(r.data); })
      .catch(() => toast.error("Failed to load wallets"))
      .finally(() => setPageLoading(false));

  useEffect(() => { load(); }, []);

  // WES-A11Y-M1: close modal on Escape
  useEffect(() => {
    if (!showTopup) return;
    const onKey = (e) => { if (e.key === "Escape") setShowTopup(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showTopup]);

  // WES-PRI-05: announce toggle to screen readers
  const handlePrivacyToggle = () => {
    setPrivacyMsg(privacyMode ? "Balances visible" : "Balances hidden");
    togglePrivacy();
  };

  // WES-BAL-B1-CALC: USD equivalent of all wallets combined
  const ratesMap = useMemo(
    () => Object.fromEntries(rates.map(r => [r.target, r.rate])),
    [rates],
  );

  const totalUsd = useMemo(() => {
    if (!wallets.length || !Object.keys(ratesMap).length) return null;
    return wallets.reduce((acc, w) => {
      const r = ratesMap[w.currency];
      return r ? acc + w.balance / r : acc;
    }, 0);
  }, [wallets, ratesMap]);

  // WES-CUR-P1: portfolio composition segments
  const portfolio = useMemo(() => {
    if (!wallets.length || !Object.keys(ratesMap).length) return [];
    const items = wallets
      .filter(w => w.balance > 0)
      .map(w => ({
        currency: w.currency,
        usd: ratesMap[w.currency] ? w.balance / ratesMap[w.currency] : 0,
        accent: WALLET_ACCENT[w.currency] || "#10B981",
      }))
      .filter(i => i.usd > 0);
    const total = items.reduce((s, i) => s + i.usd, 0);
    return total > 0 ? items.map(i => ({ ...i, pct: (i.usd / total) * 100 })) : [];
  }, [wallets, ratesMap]);

  const activeCount = wallets.filter(w => w.balance > 0).length;

  // WES-CUR-C1: sort wallets by canonical priority order
  const sortedWallets = useMemo(() => {
    if (!wallets.length) return [];
    return [...wallets].sort((a, b) => {
      const ai = CURRENCY_ORDER.indexOf(a.currency);
      const bi = CURRENCY_ORDER.indexOf(b.currency);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [wallets]);

  // WES-QA Add money: open modal with currency pre-set
  // WES-A11Y-M2: store trigger element so focus can return on close
  const openTopup = (currency = "GBP", triggerEl = null) => {
    topupTriggerRef.current = triggerEl || document.activeElement;
    setTopupCurrency(currency);
    setShowTopup(true);
  };

  const closeTopup = () => {
    setShowTopup(false);
    setTimeout(() => topupTriggerRef.current?.focus(), 50);
  };

  const fmtTotal = totalUsd !== null
    ? `$${totalUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

  return (
    <div className="space-y-7">

      {/* WES-PRI-05: screen reader live region for privacy toggle */}
      <span role="status" aria-live="polite" className="sr-only">{privacyMsg}</span>

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -14, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">
            Multi-currency
          </p>
          <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">
            Your wallets
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* WES-PRI-01 */}
          <button
            data-testid="wallet-privacy-toggle"
            onClick={handlePrivacyToggle}
            aria-label={privacyMode ? "Show balances" : "Hide balances"}
            aria-pressed={privacyMode}
            className="w-9 h-9 rounded-xl border border-border/60 grid place-items-center text-muted-foreground hover:text-navy hover:bg-secondary/60 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            {privacyMode ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
          <button
            data-testid="wallet-topup-cta"
            onClick={() => openTopup()}
            className="btn-primary shrink-0"
          >
            <Plus size={14} strokeWidth={2.5} /> Add money
          </button>
        </div>
      </motion.div>

      {/* ── Portfolio Hero (WES-BAL-T1) ─────────────────────────── */}
      <motion.div
        data-testid="wallet-portfolio-hero"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: ez, delay: 0.04 }}
        className="brand-gradient rounded-3xl p-6 sm:p-8 relative overflow-hidden"
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(16,185,129,0.16)" }} aria-hidden="true" />
        <div className="absolute -bottom-16 -left-12 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(6,182,212,0.09)" }} aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" aria-hidden="true" />

        <div className="relative">
          <div className="flex items-start justify-between mb-1">
            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold">
              Total portfolio · USD equivalent
            </p>
            <div className="w-10 h-10 rounded-2xl bg-white/[0.07] border border-white/10 grid place-items-center" aria-hidden="true">
              <WalletIcon size={16} className="text-white/55" />
            </div>
          </div>

          {/* WES-BAL-B1: Total balance + privacy */}
          <div
            data-testid="wallet-total-balance"
            aria-label={privacyMode ? "Balance hidden" : `Total portfolio: ${fmtTotal}`}
          >
            {pageLoading ? (
              <div className="skeleton-dark h-14 w-48 rounded-xl mt-2 mb-1" aria-hidden="true" />
            ) : (
              <AnimatePresence mode="wait">
                {privacyMode ? (
                  <motion.p
                    key="hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: priDuration }}
                    aria-hidden="true"
                    className="font-display text-[2.6rem] sm:text-5xl font-black text-white num-tabular tracking-tight mt-1 leading-none mb-1"
                  >
                    ••••••
                  </motion.p>
                ) : (
                  <motion.p
                    key="visible"
                    initial={{ opacity: 0, y: reducedMotion ? 0 : 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: reducedMotion ? 0 : 0.4, ease: ez }}
                    className="font-display text-[2.6rem] sm:text-5xl font-black text-white num-tabular tracking-tight mt-1 leading-none mb-1"
                  >
                    {fmtTotal}
                  </motion.p>
                )}
              </AnimatePresence>
            )}
          </div>

          <p className="text-[12px] text-white/40 mb-5">
            {activeCount} wallet{activeCount !== 1 ? "s" : ""} active
          </p>

          {/* WES-CUR-P1: composition bar */}
          {!pageLoading && portfolio.length > 0 && (
            <div className="mb-5" aria-hidden="true">
              <div className="flex h-1.5 rounded-full overflow-hidden gap-0.5 mb-2">
                {portfolio.map((p, i) => (
                  <motion.div
                    key={p.currency}
                    className="h-full rounded-full"
                    style={{ background: p.accent }}
                    initial={{ width: reducedMotion ? `${p.pct}%` : 0 }}
                    animate={{ width: `${p.pct}%` }}
                    transition={{ duration: reducedMotion ? 0 : 0.7, ease: ez, delay: reducedMotion ? 0 : 0.3 + i * 0.08 }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {portfolio.map(p => (
                  <div key={p.currency} className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: p.accent }} />
                    <span className="text-[10px] font-bold text-white/45">
                      {p.currency} {p.pct.toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* WES-QA-H1: Hero quick actions */}
          <div className="flex flex-wrap gap-2">
            <Link
              to="/app/send"
              data-testid="wallet-hero-send"
              className="btn-primary text-sm py-2.5 px-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <Send size={13} strokeWidth={2.2} /> Send
            </Link>
            <Link
              to="/app/receive"
              data-testid="wallet-hero-receive"
              className="btn-ghost text-sm py-2.5 px-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ArrowDownLeft size={13} /> Receive
            </Link>
            <button
              onClick={() => openTopup()}
              data-testid="wallet-hero-topup"
              className="btn-ghost text-sm py-2.5 px-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <Plus size={13} /> Add money
            </button>
          </div>
        </div>
      </motion.div>

      {/* ── Stats row ───────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38, ease: ez, delay: 0.08 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
      >
        {[
          { label: "Active wallets", value: `${activeCount} of 7`, testid: "wallet-stat-active" },
          { label: "Currencies",     value: wallets.length,         testid: "wallet-stat-currencies" },
          { label: "Countries",      value: "45+",                  testid: "wallet-stat-countries" },
        ].map(({ label, value, testid }) => (
          <div key={label} className="card-float px-4 py-3.5" data-testid={testid}>
            <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{label}</p>
            <p className="font-display text-lg font-black text-navy num-tabular mt-0.5">{value}</p>
          </div>
        ))}
      </motion.div>

      {/* ── Wallet grid ─────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-black text-navy tracking-tight">Wallets</h2>
          <button
            onClick={load}
            aria-label="Refresh wallets"
            data-testid="wallet-refresh"
            className="w-8 h-8 rounded-xl grid place-items-center text-muted-foreground hover:text-navy hover:bg-secondary/60 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <RefreshCw size={14} />
          </button>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          aria-busy={pageLoading}
          data-testid="wallet-grid"
        >
          {pageLoading
            ? Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="skeleton h-52 rounded-3xl" aria-label="Loading wallet…" />
              ))
            : sortedWallets.map((w, i) => (
                <WalletCard
                  key={w.currency}
                  wallet={w}
                  index={i}
                  privacyMode={privacyMode}
                  priDuration={priDuration}
                  reducedMotion={reducedMotion}
                  onAddFunds={openTopup}
                />
              ))}
        </div>
      </div>

      {/* ── CTA card ────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: ez, delay: 0.16 }}
        className="card-elevated p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-emerald/10 grid place-items-center text-emerald shrink-0">
            <TrendingUp size={18} />
          </div>
          <div>
            <p className="font-display font-black text-navy text-[15px] tracking-tight">
              Ready to move money?
            </p>
            <p className="text-sm text-muted-foreground">
              Send instantly from any wallet to a beneficiary in 45+ countries.
            </p>
          </div>
        </div>
        <Link
          to="/app/send"
          data-testid="wallet-send-link"
          className="btn-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <Send size={13} /> Send money
        </Link>
      </motion.div>

      {/* ── Add money (real, provider-backed funding) ───────────── */}
      <FundWalletModal
        open={showTopup}
        onClose={closeTopup}
        defaultCurrency={topupCurrency}
        onFunded={() => { closeTopup(); load(); }}
      />
    </div>
  );
}

// WES-CARD: Individual wallet card — handles State 1 (positive) and State 2 (zero)
function WalletCard({ wallet, index, privacyMode, priDuration, reducedMotion, onAddFunds }) {
  const c = CURRENCY_BY_CODE[wallet.currency];
  if (!c) return null;
  const accent = WALLET_ACCENT[wallet.currency] || "#10B981";
  const isEmpty = wallet.balance === 0;
  const balance = formatAmount(wallet.balance, wallet.currency);

  return (
    <motion.div
      data-testid={`wallet-card-${wallet.currency}`}
      role="article"
      aria-label={`${c.name} wallet, ${privacyMode ? "balance hidden" : balance}`}
      className={`p-5 relative overflow-hidden group ${isEmpty ? "card-elevated" : "card-float"}`}
      initial={{ opacity: 0, y: reducedMotion ? 0 : 16, scale: reducedMotion ? 1 : 0.95 }}
      animate={{ opacity: 1, y: 0,  scale: 1 }}
      transition={{ duration: reducedMotion ? 0 : 0.45, ease: ez, delay: reducedMotion ? 0 : 0.06 + index * 0.05 }}
      whileHover={reducedMotion ? {} : { y: isEmpty ? -2 : -4 }}
    >
      {/* State 1: ambient glow */}
      {!isEmpty && (
        <div
          className="absolute -top-10 -right-10 w-36 h-36 rounded-full blur-2xl opacity-[0.14] group-hover:opacity-[0.22] transition-opacity duration-500 pointer-events-none"
          style={{ background: accent }}
          aria-hidden="true"
        />
      )}
      {/* State 2: top accent strip */}
      {isEmpty && (
        <div
          className="absolute top-0 left-0 right-0 h-0.5 pointer-events-none"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, opacity: 0.3 }}
          aria-hidden="true"
        />
      )}

      <div className="relative">
        {/* Header row */}
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-12 h-12 rounded-2xl grid place-items-center text-[22px]"
            style={{ background: `${accent}${isEmpty ? "0D" : "16"}` }}
            aria-hidden="true"
          >
            {c.flag}
          </div>
          <span
            className="text-[10px] font-bold px-2.5 py-1 rounded-full"
            style={{
              background: `${accent}${isEmpty ? "0A" : "14"}`,
              color: isEmpty ? `${accent}99` : accent,
            }}
            aria-hidden="true"
          >
            {c.code}
          </span>
        </div>

        {/* WES-BAL-B2: Spendable balance with privacy mode (WES-PRI-04) */}
        <AnimatePresence mode="wait">
          {privacyMode ? (
            <motion.p
              key="hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: priDuration }}
              aria-hidden="true"
              className={`font-display text-[1.65rem] font-black num-tabular tracking-tight ${isEmpty ? "text-muted-foreground/30" : "text-navy"}`}
            >
              ••••••
            </motion.p>
          ) : (
            <motion.p
              key="visible"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: priDuration }}
              className={`font-display text-[1.65rem] font-black num-tabular tracking-tight ${isEmpty ? "text-muted-foreground/30" : "text-navy"}`}
            >
              {balance}
            </motion.p>
          )}
        </AnimatePresence>

        <p className={`text-[11px] mt-0.5 ${isEmpty ? "text-muted-foreground/40" : "text-muted-foreground"}`}>
          {c.name}
          {isEmpty && <span className="ml-1.5 text-[10px]">· No funds</span>}
        </p>

        {/* WES-QA: Quick actions */}
        <div className="mt-4 flex items-center gap-1.5">
          {isEmpty ? (
            // State 2: Add funds only
            <button
              onClick={() => onAddFunds(wallet.currency)}
              data-testid={`wallet-add-${wallet.currency}`}
              aria-label={`Add funds to ${c.name} wallet`}
              className="flex-1 flex items-center justify-center gap-1 text-[11px] font-bold py-2 rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2"
              style={{
                background: `${accent}0D`,
                color: accent,
                border: `1px dashed ${accent}40`,
              }}
            >
              <Plus size={10} strokeWidth={2.5} /> Add funds
            </button>
          ) : (
            // State 1: Send + Receive
            <>
              <Link
                to={`/app/send?from=${wallet.currency}`}
                data-testid={`wallet-send-${wallet.currency}`}
                aria-label={`Send ${c.name}`}
                className="flex-1 flex items-center justify-center gap-1 text-[11px] font-bold py-2 rounded-xl border border-border/60 hover:border-emerald/40 hover:text-emerald hover:bg-emerald/[0.05] text-muted-foreground transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                <ArrowUpRight size={10} strokeWidth={2.5} /> Send
              </Link>
              <Link
                to="/app/receive"
                data-testid={`wallet-receive-${wallet.currency}`}
                aria-label={`Receive ${c.name}`}
                className="flex-1 flex items-center justify-center gap-1 text-[11px] font-bold py-2 rounded-xl border border-border/60 hover:border-navy/30 hover:text-navy hover:bg-navy/[0.04] text-muted-foreground transition-all focus-visible:outline-none focus-visible:ring-2"
              >
                <ArrowDownLeft size={10} strokeWidth={2.5} /> Receive
              </Link>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
